import { useContext } from "react";

import { Link } from "react-router-dom";
import { ChartsContext } from "../components/ChartsContext";
import { Table, TableBody, TableContainer, TableCell, TableHead, TableRow, Unstable_Grid2 as Grid, Box, Typography } from "@mui/material";
import { DimensionCard, ProgressBar, ReturnBar, SentimentBar } from "../components/ui";
import { MutatingDots } from "react-loader-spinner";
import { palette } from "../../assets/styles/theme";

export default function Overview(): JSX.Element {
  const chartsContext = useContext(ChartsContext);

  const { isLoading, errorMessage, survey, questions } = chartsContext;

  let surveyScore = -1;
  if (survey.dimensions.length > 0) {
    surveyScore =
      survey.dimensions
        .map((dimension) => dimension.score)
        .reduce((previousValue, currentValue) => previousValue + currentValue) /
      survey.dimensions.length;
  }

  /*
  let surveyParticipation = -1;
  console.log(questions)
   if (
    questions !== undefined && 
    questions.length > 0 &&
    (questions[0] as Type1Question).choiceValues?.length > 0
  ) {
    surveyParticipation = (questions[0] as Type1Question).choiceValues.reduce(
      (previousValue, currentValue) => previousValue + currentValue
    );
  }
  */

  let surveyMaxScore = -1;
  if (survey.dimensions.length > 0) {
    surveyMaxScore =
      survey.dimensions
        .map((dimension) => dimension.maxScore)
        .reduce((previousValue, currentValue) => previousValue + currentValue) /
      survey.dimensions.length;
  }

  return (
    <>
      <Box padding={"20px"} bgcolor={"#F9F9F9"}>
        <ReturnBar title={survey.templateName || survey.title} fromOverview={true} />

        {errorMessage}
        {isLoading ? (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
                bgcolor: 'rgba(250,251,252,1)', // blanco muy sutil
              zIndex: 2000,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backdropFilter: 'blur(2px)',
            }}
          >
            <MutatingDots
              color="#1A237E"
              secondaryColor={palette.primary}
              height="100"
              width="100"
              visible={true}
            />
            <Typography mt={2} fontWeight={500} fontFamily="IBM Plex Sans" color="#1A237E" fontSize={16}>
              Loading survey...
            </Typography>
          </Box>
        ) : ""}

        {!isLoading && (
          <Box>
            <Grid
              container
              spacing={2}
              sx={{
                marginTop: 1,
                marginBottom: 1.5,
              }}
            >
              <Grid xs={12} sm={6} md={3}>
                <DimensionCard
                  title={
                    survey.templateId === 4 
                      ? "ENPS Score" 
                      : survey.templateName === "Employee Burnout"
                        ? "Burnout Score"
                        : survey.templateName + " Score"
                  }
                  score={surveyScore}
                  maxScore={surveyMaxScore}
                />
              </Grid>
              {survey.dimensions.map((dimension) => {
                if (dimension.name !== "") {
                  return (
                    <Grid key={dimension.name} xs={12} sm={6} md={3}>
                      {survey.templateName !== "Employee Burnout" ? (
                        <DimensionCard
                          title={dimension.name}
                          score={dimension.score}
                          maxScore={dimension.maxScore}
                        />
                      ) : (
                        <DimensionCard
                          title={dimension.name}
                          score={dimension.score}
                          maxScore={dimension.maxScore}
                          reversedColor={true}
                        />
                      )}
                    </Grid>
                  );
                }
                return "";
              })}
              <Grid xs={12} sm={6} md={3}>
                <DimensionCard
                  title={"Participation"}
                  score={survey.numberOfResponses}
                  round
                />
              </Grid>
            </Grid>

            <Box
              alignItems={"center"}
              sx={{
                background: '#fff', // mismo fondo que ReturnBar y DimensionCard
                border: '1.5px solid #e0e0e0',
                borderRadius: '12px',
                boxShadow: 'none',
                padding: '10px',
              }}
            >
              <Grid
                container
                spacing={1}
                justifyContent={"space-between"}
                sx={{
                  marginY: 1,
                  paddingLeft: '20px',
                }}
              >
                <Box>
                  <Typography
                    fontSize={"20px"}
                    fontFamily={"IBM Plex Sans"}
                    fontWeight={600}
                    color={"#1A237E"}
                  >
                    Questions
                  </Typography>
                  <Link to={`/charts/${survey.id}/questions-data`} style={{ fontFamily: 'IBM Plex Sans' }}>
                    View all questions data
                  </Link>
                </Box>
                {/*<Link to={`/charts/${survey.id}/comparative-information`}>
                  Dimensions
              </Link>*/}
              </Grid>
              <TableContainer
                sx={{
                  borderRadius: 1,
                  boxShadow: 0,
                  margin: 1,
                  width: "calc(100% - 16px)",
                  overflowX: "auto"
                }}
              >
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                      <TableCell
                        sx={{
                          width: { xs: "50%", sm: "70%", md: "80%" },
                          color: "#1A237E",
                          fontSize: { xs: "14px", md: "16px" },
                          fontFamily: "IBM Plex Sans",
                          fontWeight: 600,
                        }}
                      >
                        Question
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "10%",
                          color: "#1A237E",
                          fontSize: { xs: "14px", md: "16px" },
                          fontFamily: "IBM Plex Sans",
                          fontWeight: 600,
                        }}
                      >
                        Results
                      </TableCell>
                      {survey.templateId !== 4 && (
                        <TableCell
                          sx={{
                            width: "10%",
                            color: "#1A237E",
                            fontSize: { xs: "14px", md: "16px" },
                            fontFamily: "IBM Plex Sans",
                            fontWeight: 600,
                          }}
                        >
                          Dimension
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {questions.map((question, index) => (
                      <TableRow key={question.id}>
                        <TableCell
                          sx={{
                            fontSize: { xs: "14px", md: "16px" },
                            fontFamily: "IBM Plex Sans",
                            fontWeight: 500,
                          }}
                        >
                          {index + 1}. {question.title}
                        </TableCell>
                        <TableCell>
                          {question.type === "checkbox" ||
                          question.type === "dropdown" ||
                          question.type === "emotionsratings" ||
                          question.type === "imagepicker" ||
                          question.type === "matrix" ||
                          question.type === "radiogroup" ||
                          question.type === "rating" ? (
                            survey.templateName !== "Employee Burnout" ? (
                              <ProgressBar
                                score={question.score}
                                maxScore={question.maxScore}
                              />
                            ) : (
                              <ProgressBar
                                score={question.score}
                                maxScore={question.maxScore}
                                reversedColor
                              />
                            )
                          ) : (
                            question.type === "comment" ||
                            (question.type === "text" && (
                              <SentimentBar score={question.score} />
                            ))
                          )}
                        </TableCell>
                        {survey.templateId !== 4 && (
                          <TableCell
                            sx={{
                              fontSize: "16px",
                              fontFamily: "IBM Plex Sans",
                              fontWeight: "Regular",
                            }}
                          >
                            {question.dimension}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
